/* One model at a time. The panels are rendered by Liquid — this only decides
   which one shows, keeps the tabs and the manual button in step with it,
   remembers the choice in the URL so a link to ?model=OC200 opens on OC200,
   and swaps a YouTube how-to's thumbnail for its player when tapped. */
class OracuraGuide extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    // The list only feeds the manual buttons and the ?model= link. If it ever
    // fails to parse, the tabs still switch panels rather than going dead.
    try {
      this.models = JSON.parse(this.querySelector('[data-guide-data]')?.textContent || '[]');
    } catch {
      this.models = [];
    }
    this.tabs = [...this.querySelectorAll('[data-guide-model]')];
    this.panels = [...this.querySelectorAll('[data-guide-panel]')];
    this.addEventListener('click', event => {
      const youtube = event.target.closest('[data-guide-youtube]');
      if (youtube) {
        event.preventDefault();
        this.play(youtube);
        return;
      }
      const tab = event.target.closest('[data-guide-model]');
      if (tab) this.select(Number(tab.dataset.guideModel));
    });
    this.addEventListener('keydown', event => {
      const index = this.tabs.indexOf(event.target);
      if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
      this.select(Number(this.tabs[next].dataset.guideModel));
      this.tabs[next].focus();
    });
    // A ?model= link wins; otherwise the model Liquid opened on, the first tab.
    const selected = new URL(location.href).searchParams.get('model');
    const linked = this.models.find(model => model.label === selected);
    const opening = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    const index = linked ? linked.index : Number(opening?.dataset.guideModel);
    if (Number.isInteger(index)) this.select(index, false);
  }
  // The YouTube link becomes the player in place. Nothing from YouTube loads
  // before this, so a page of models costs no embeds until one is asked for.
  play(link) {
    const frame = document.createElement('iframe');
    frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(link.dataset.guideYoutube)}?autoplay=1&rel=0&playsinline=1`;
    frame.title = link.getAttribute('aria-label') || '';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    frame.allowFullscreen = true;
    link.hidden = true;
    link.after(frame);
    frame.focus();
  }
  // `index` is the block's position in the section, which numbers the tabs,
  // the panels and the list alike — not a position in the list, which skips
  // models whose product is unavailable.
  select(index, updateUrl = true) {
    if (!this.tabs.some(tab => Number(tab.dataset.guideModel) === index)) return;
    this.tabs.forEach(tab => {
      const active = Number(tab.dataset.guideModel) === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach(panel => {
      const active = Number(panel.dataset.guidePanel) === index;
      panel.hidden = !active;
      // A video left playing behind a hidden panel keeps its audio going. The
      // one coming into view fetches its metadata, so the duration shows on the
      // controls before anyone presses play; the others stay at preload=none.
      panel.querySelectorAll('video').forEach(video => active ? (video.preload = 'metadata') : video.pause());
      // A YouTube player cannot be paused from here without its API, so it is
      // put back to its thumbnail instead.
      if (!active) {
        panel.querySelectorAll('[data-guide-youtube][hidden]').forEach(link => {
          link.nextElementSibling?.remove();
          link.hidden = false;
        });
      }
    });
    const model = this.models.find(entry => entry.index === index);
    if (!model?.url) return;
    // A model that ships as a LITE and a PRO has a manual each, so the one
    // button becomes two. Copy comes off the host element rather than from
    // here, so it stays editable in the theme editor.
    const manual = this.querySelector('[data-guide-manual]');
    const manualPro = this.querySelector('[data-guide-manual-pro]');
    const pair = Boolean(model.manual && model.manualPro);

    manual.href = model.manual || model.url;
    manual.textContent = model.manual
      ? (pair ? this.dataset.manualLiteLabel || 'LITE Manual' : this.dataset.manualLabel || 'Download Manual')
      : this.dataset.instructionsLabel || 'Product Instructions';
    manual.target = model.manual ? '_blank' : '_self';
    manual.rel = 'noopener';

    if (manualPro) {
      manualPro.hidden = !pair;
      if (pair) {
        manualPro.href = model.manualPro;
        manualPro.textContent = this.dataset.manualProLabel || 'PRO Manual';
      }
    }
    if (updateUrl) {
      const url = new URL(location.href);
      url.searchParams.set('model', model.label);
      history.replaceState(null, '', url);
    }
  }
}
if (!customElements.get('oracura-guide')) customElements.define('oracura-guide', OracuraGuide);
